import {
	AfterViewInit,
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { DataSetSearchComponent } from 'src/app/components/data-set/data-set-search.component';
import { UserProfile } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { MarkerInfo } from 'src/app/components/data-set/data-set-map.component';
import { contractStatusDropdownOptions } from 'src/app/models/billingContractAndOptions';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import Contract = Components.Schemas.Contract;
import { EndBillingDialogComponent } from '../end-billing-dialog/end-billing-dialog.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { DataSetSearchComponent as DataSetSearchComponent_1 } from '../../../components/data-set/data-set-search.component';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import {
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import {
	DataSetTableComponent,
	DataSetTableMenuComponent,
	DataSetRowClickDirective,
} from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BillingStatusComponent } from '../../../components/billing-status/billing-status.component';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import {
	AccessDatePlaceholderPipe,
	AccessDatePipe,
} from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';
import { formatDate } from '@angular/common';

@Component({
	selector: 'app-billing-list-table',
	templateUrl: './billing-list-table.component.html',
	styleUrls: ['./billing-list-table.component.scss', '../../common/main.scss'],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent_1,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		BillingStatusComponent,
		DataSetTableMenuComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetRowClickDirective,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		AccessDatePlaceholderPipe,
		AccessDatePipe,
		DataSetLoadingRowComponent,
	],
})
export class BillingListTableComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@ViewChild(DataSetSearchComponent) dataSetSearch!: DataSetSearchComponent;
	@ViewChild(MatSort, { static: false }) sort!: MatSort;
	@ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

	displayedColumns = [
		'companyName',
		'contractName',
		'status',
		'billingStartDate',
		'duration',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<Contract>();
	activeCompanyId: string | undefined = '';
	localizeContractName = $localize`:@@billingListTable-contractNameLabel:Contract Name`;
	markersInfo: MarkerInfo[] = [];

	private filterSubscription?: Subscription;

	getBillingContractsLoadingKey = ApiLoadingKeys.getContracts;
	finishedLoadingInitData: boolean = false;

	filterLabels = {
		defaultPlaceholder: $localize`:@@billingListTable-defaultPlaceholder:Search companies...`,
		companyName: $localize`:@@billingListTable-filterLabelCompanyNameLabel:Company Name`,
		contractName: $localize`:@@billingListTable-filterLabelContractNameLabel:Contract Name`,
		status: $localize`:@@billingListTable-filterLabelStatusLabel:Status`,
		billingStartDate: $localize`:@@billingListTable-filterLabelBillingStartDateLabel:Billing Start Date`,
		duration: $localize`:@@billingListTable-filterLabelDurationLabel:Duration`,
		activeDate: $localize`:@@billingListTable-filterLabelActiveDateLabel:Active Date`,
	};

	contractStatusDropdownOptions = contractStatusDropdownOptions;

	constructor(
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly scService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService,
		private readonly dialog: MatDialog
	) {}

	async ngOnInit() {
		await this.setActiveCompanyId();
	}

	ngAfterViewInit(): void {
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					filter = this.convertFilterDateToISO(filter);
					const result = await this.apiService.getBillingContracts(filter);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((response) => {
				this.dataSource.data = response;
			});
	}

	convertFilterDateToISO(filter: QueryParam[]) {
		return filter.map((item) =>
			['billingStartDate', 'activeDate'].includes(item.param) &&
			item.value instanceof Date
				? {
						...item,
						value: formatDate(item.value, 'yyyy-MM-dd', 'en-US'),
					}
				: item
		);
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	onAddButtonClick(): void {
		this.router.navigateByUrl('/add-billingcontract', {
			state: { companyId: this.activeCompanyId },
		});
	}

	onRowClick(contract: Contract): void {
		this.router.navigate([`/billingcontract-details/view/${contract.id}`]);
	}

	onEditClick(contract: Contract): void {
		this.router.navigate([`/billingcontract-details/edit/${contract.id}`]);
	}

	async onEndBillingClick(contract: Contract) {
		const dialogRef: MatDialogRef<EndBillingDialogComponent> = this.dialog.open(
			EndBillingDialogComponent,
			{
				maxWidth: '510px',
				panelClass: 'pop-up-dialog-v2',
				data: {
					id: contract.id,
					companyName: contract.company.name,
				},
			}
		);

		dialogRef.afterClosed().subscribe((result: any) => {
			if (!!result) {
				contract.status = 'manuallyEnded';
			}
		});
	}

	async setActiveCompanyId(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id;
	}

	rowId(row: Contract): string {
		return row?.id || 'unknown-id';
	}
}
